import { z } from 'zod'
import { auditFieldsSchema, contactEmailSchema, contactPhoneSchema, uuidSchema } from './common.ts'

const nullableText = z.string().trim().min(1).nullable()

/**
 * A person, independent of any company (ADR 0004 D1). What is true of the human lives here; how
 * they relate to a company lives on the link.
 */
export const contactSchema = z
  .object({
    id: uuidSchema,
    fullName: z.string().min(1),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    note: z.string().nullable(),
  })
  .extend(auditFieldsSchema.shape)

export type Contact = z.infer<typeof contactSchema>

export const createContactInputSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  phone: contactPhoneSchema.nullable(),
  email: contactEmailSchema.nullable(),
  note: nullableText,
})

export type CreateContactInput = z.infer<typeof createContactInputSchema>

export const updateContactInputSchema = createContactInputSchema
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateContactInput = z.infer<typeof updateContactInputSchema>

/**
 * The company side of a person's link. `role` belongs here rather than on the person: the same
 * human is a director at one client and a consultant at another.
 */
export const clientContactSchema = z
  .object({
    id: uuidSchema,
    clientId: uuidSchema,
    contactId: uuidSchema,
    role: z.string().nullable(),
    isPrimary: z.boolean(),
  })
  .extend(auditFieldsSchema.shape)

export type ClientContact = z.infer<typeof clientContactSchema>

/** A person as seen from a company: the person, plus the facts of this particular link. */
export const companyContactSchema = contactSchema.extend({
  linkId: uuidSchema,
  role: z.string().nullable(),
  isPrimary: z.boolean(),
})

export type CompanyContact = z.infer<typeof companyContactSchema>

/** A company as seen from a person — the "attached companies" list on their record. */
export const contactCompanySchema = z.object({
  linkId: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  role: z.string().nullable(),
  isPrimary: z.boolean(),
})

export type ContactCompany = z.infer<typeof contactCompanySchema>

/** A person in the register, with the companies they are attached to. */
export const contactWithCompaniesSchema = contactSchema.extend({
  companies: z.array(contactCompanySchema),
})

export type ContactWithCompanies = z.infer<typeof contactWithCompaniesSchema>

export const attachContactInputSchema = z.object({
  contactId: uuidSchema,
  role: nullableText.optional(),
  isPrimary: z.boolean().default(false),
})

export type AttachContactInput = z.infer<typeof attachContactInputSchema>

export const updateClientContactInputSchema = z
  .object({ role: nullableText, isPrimary: z.boolean() })
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateClientContactInput = z.infer<typeof updateClientContactInputSchema>

/** Creating a person and attaching them in one step — the flow from a company or a deal. */
export const createAndAttachContactInputSchema = createContactInputSchema.extend({
  role: nullableText.optional(),
  isPrimary: z.boolean().default(false),
})

export type CreateAndAttachContactInput = z.infer<typeof createAndAttachContactInputSchema>

/** Kept for client creation, which still takes one person inline. */
export const createPrimaryContactInputSchema = createContactInputSchema.extend({
  role: nullableText,
})

export type CreatePrimaryContactInput = z.infer<typeof createPrimaryContactInputSchema>
